/**
 * The encoded output, kept on disk in the origin private file system.
 *
 * Two jobs, and the second one is why this is a module rather than a few
 * lines inside the worker:
 *
 *  1. The encoder writes here instead of into an ArrayBuffer, because
 *     BufferTarget grows by reallocating and copying and dies on any output
 *     of real size.
 *
 *  2. It survives the page. An upload interrupted at 98% used to mean
 *     re-encoding from scratch, because the encoded bytes lived only in
 *     memory and the encoder is not deterministic enough to reproduce them
 *     byte for byte - so the half-finished stash on Commons could not be
 *     trusted against a fresh encode. Keeping the output on disk removes
 *     that argument entirely: the resume reuses the exact same bytes.
 *
 * Nothing here is load-bearing. Every function degrades to null or a no-op
 * when the browser has no OPFS, and the encoder falls back to BufferTarget.
 */

const PREFIX = "c2c-out-"

/**
 * How long an output is kept.
 *
 * Commons drops an unfinished upload stash after about six hours, so an
 * output older than that can no longer be resumed into anything and is only
 * taking up the contributor's disk.
 */
const STALE_MS = 6 * 60 * 60 * 1000

interface FileHandleLike {
  createWritable: () => Promise<WritableStream>
  getFile: () => Promise<File>
}

interface DirectoryHandleLike {
  getFileHandle: (name: string, opts?: { create?: boolean }) => Promise<FileHandleLike>
  removeEntry: (name: string) => Promise<void>
  keys?: () => AsyncIterableIterator<string>
}

async function root(): Promise<DirectoryHandleLike | null> {
  try {
    const storage = (navigator as unknown as {
      storage?: { getDirectory?: () => Promise<DirectoryHandleLike> }
    }).storage
    if (!storage?.getDirectory) return null
    return await storage.getDirectory()
  } catch {
    return null
  }
}

/**
 * Drop outputs from runs that were cancelled, crashed, or finished long
 * enough ago that their Commons stash has expired.
 *
 * The current run's output is deliberately spared: the Blob handed to the
 * uploader reads straight out of it, so deleting it on completion would pull
 * the file out from under an upload in progress. The next encode clears it.
 */
export async function sweepStaleOutputs(now = Date.now()): Promise<void> {
  const dir = await root()
  if (!dir?.keys) return
  const cutoff = now - STALE_MS
  try {
    for await (const name of dir.keys()) {
      if (!name.startsWith(PREFIX)) continue
      const stamp = Number(name.slice(PREFIX.length).split("-")[0])
      if (Number.isFinite(stamp) && stamp < cutoff) {
        try { await dir.removeEntry(name) } catch { /* another tab may hold it */ }
      }
    }
  } catch {
    // Sweeping is housekeeping; never let it fail an encode.
  }
}

export interface EncodedTarget {
  /** Pass to mediabunny's StreamTarget. */
  writable: WritableStream
  /** Identifies this output later. Goes into the resume entry. */
  key: string
  /** The finished file. Only valid once the output has been finalized. */
  collect: () => Promise<File>
}

/**
 * Open a new output file. Returns null when the browser has no usable OPFS,
 * in which case the caller falls back to an in-memory buffer.
 */
export async function createEncodedFile(extension: string): Promise<EncodedTarget | null> {
  const dir = await root()
  if (!dir) return null
  try {
    await sweepStaleOutputs()
    const key = PREFIX + Date.now() + "-" +
      Math.random().toString(36).slice(2, 8) + extension
    const handle = await dir.getFileHandle(key, { create: true })
    const writable = await handle.createWritable()
    return { writable, key, collect: () => handle.getFile() }
  } catch {
    // Quota refused, private browsing, or no write access.
    return null
  }
}

/**
 * Re-open an output written by an earlier run, so an interrupted upload can
 * carry on with the exact bytes Commons already holds part of. Returns null
 * when it has been swept, the browser has no OPFS, or the key is empty.
 */
export async function openEncodedFile(key: string | undefined): Promise<File | null> {
  if (!key || !key.startsWith(PREFIX)) return null
  const dir = await root()
  if (!dir) return null
  try {
    const handle = await dir.getFileHandle(key)
    const file = await handle.getFile()
    return file.size > 0 ? file : null
  } catch {
    return null
  }
}

/** Drop one output once its upload has been published. */
export async function deleteEncodedFile(key: string | undefined): Promise<void> {
  if (!key || !key.startsWith(PREFIX)) return
  const dir = await root()
  if (!dir) return
  try { await dir.removeEntry(key) } catch { /* already gone */ }
}
